import Vue from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import UrlHandlerApiService from "./url-handler.service";

/**
 * Supplier API Service for Product Catalog and Category Mapping
 * Handles all supplier-related API requests including products, categories, and filters
 */
const SupplierApiService = {
  URL: "",
  async init() {
    Vue.use(VueAxios, axios);
    Vue.axios.defaults.headers.common["Content-Type"] = "application/json";
    const resp = await Vue.axios.get(`api/url/get-base-uri`);
    this.URL = resp.data;
  },

  // ==================== PRODUCT CATALOG ENDPOINTS ====================

  /**
   * Get supplier products list with filtering and pagination
   * GET /v1/supplierProduct/list
   * @param {Object} params - Query parameters
   * @param {number} params.page - Current page (default: 1)
   * @param {number} params.maxResults - Items per page (default: 25, max: 100)
   * @param {string} params.categories - Comma-separated category IDs
   * @param {string} params.brand - Brand name filter
   * @param {number} params.fromCostPrice - Minimum cost price
   * @param {number} params.toCostPrice - Maximum cost price
   * @param {boolean} params.hasQuantity - Filter products with quantity > 0
   * @returns {Promise} API response with products list
   */
  async getSupplierProducts(params = {}) {
    try {
      const queryParams = this._buildProductQueryParams(params);
      const response = await Vue.axios.get(`${this.URL}api/dropshipping/v1/supplierProduct/list`, {
        params: queryParams,
        paramsSerializer: function(params) {
          const searchParams = new URLSearchParams();

          Object.keys(params).forEach(key => {
            if (key === "categories" && Array.isArray(params[key])) {
              // Handle categories as separate parameters: &categories=2727&categories=2728
              params[key].forEach(categoryId => {
                searchParams.append("categories", categoryId);
              });
            } else if (params[key] !== null && params[key] !== undefined) {
              searchParams.append(key, params[key]);
            }
          });

          return searchParams.toString();
        }
      });
      return response;
    } catch (error) {
      console.error("Error fetching supplier products:", error);
      throw new Error(`[SupplierApiService] getSupplierProducts: ${error.message}`);
    }
  },

  /**
   * Update supplier product
   * PUT /v1/supplierProduct/update
   * @param {Object} productData - Product data to update
   * @param {string} productData.id - Product ID
   * @param {string} productData.name - Product name
   * @param {number} productData.price - Product price
   * @param {number} productData.salePrice - Product sale price
   * @param {boolean} productData.isPublished - Published status
   * @param {string} productData.sku - Product SKU
   * @param {number} productData.costPrice - Product cost price
   * @param {Array} productData.variations - Product variations array
   * @returns {Promise} API response
   */
  async updateSupplierProduct(productData) {
    try {
      this._validateProductData(productData);
      const response = await Vue.axios.put(
        `${this.URL}api/dropshipping/v1/supplierProduct/update`,
        productData
      );
      return response;
    } catch (error) {
      console.error("Error updating supplier product:", error);
      throw new Error(`[SupplierApiService] updateSupplierProduct: ${error.message}`);
    }
  },

  /**
   *
   * @param {Array<string>} productIds - List of product IDs to publish
   * @returns
   */
  async publishProduct(productIds) {
    try {
      const response = await Vue.axios.post(`${this.URL}api/dropshipping/v1/import/products`, {
        productIds: [productIds]
      });
      return response;
    } catch (error) {
      console.error("Error updating supplier product:", error);
      throw new Error(`[SupplierApiService] publishProduct: ${error.message}`);
    }
  },

  // ==================== PRODUCT IMPORT ENDPOINTS ====================

  /**
   * Get product import log
   * GET /v1/import/status
   * @returns {Promise} API response with product import log
   */
  async getProductImportLog() {
    try {
      const response = await Vue.axios.get(`${this.URL}api/dropshipping/v1/import/status`);
      return response;
    } catch (error) {
      console.error("Error fetching product import log:", error);
      throw new Error(`[SupplierApiService] getProductImportLog: ${error.message}`);
    }
  },

  /**
   * Get has ready to publish categories
   * GET /v1/import/hasReadyToPublishCategories
   * @returns {Promise} API response with has ready to publish categories
   */
  async hasReadyToPublishCategories() {
    try {
      const response = await Vue.axios.get(
        `${this.URL}api/dropshipping/v1/import/hasReadyToPublishCategories`
      );
      return response;
    } catch (error) {
      console.error("Error fetching product import log:", error);
      throw new Error(`[SupplierApiService] hasReadyToPublishCategories: ${error.message}`);
    }
  },

  /**
   * Publish categories
   * GET /v1/publishCategories
   * @returns {Promise} API response with publish categories
   */
  async publishCategories() {
    try {
      const response = await Vue.axios.post(
        `${this.URL}api/dropshipping/v1/import/publishCategories`
      );
      return response;
    } catch (error) {
      console.error("Error fetching product import log:", error);
      throw new Error(`[SupplierApiService] publishCategories: ${error.message}`);
    }
  },

  // ==================== CATEGORY MAPPING ENDPOINTS ====================

  /**
   * Get supplier categories list with filtering and pagination
   * GET /v1/supplierCategory/list
   * @param {Object} params - Query parameters
   * @param {number} params.page - Current page (default: 1)
   * @param {number} params.maxResults - Items per page (default: 25, max: 100)
   * @param {boolean} params.showNoMapped - Show only unmapped categories
   * @param {boolean} params.showUnmapped - Include unmapped products
   * @param {boolean} params.showReadyToPublish - Include ready to publish products
   * @param {boolean} params.showWithUnpublishedProducts - Include products with unpublished variations
   * @param {string} params.name - Category name filter
   * @param {string} params.source - Category source filter
   * @returns {Promise} API response with categories list
   */
  async getSupplierCategories(params = {}) {
    try {
      const queryParams = this._buildCategoryQueryParams(params);
      const response = await Vue.axios.get(`${this.URL}api/dropshipping/v1/supplierCategory/list`, {
        params: queryParams
      });
      return response;
    } catch (error) {
      console.error("Error fetching supplier categories:", error);
      throw new Error(`[SupplierApiService] getSupplierCategories: ${error.message}`);
    }
  },

  /**
   * Get Oobyte categories for mapping
   * GET /v1/filters/oobyteCategories
   * @returns {Promise} API response with Oobyte categories
   */
  async getOobyteCategories() {
    try {
      const response = await Vue.axios.get(
        // &countryId=140
        `${this.URL}api/main-feed/shopping-experience?cityId=1`
      );
      return response;
    } catch (error) {
      console.error("Error fetching Oobyte categories:", error);
      throw new Error(`[SupplierApiService] getOobyteCategories: ${error.message}`);
    }
  },

  /**
   * Update supplier category mapping
   * PUT /v1/supplierCategory/update
   * @param {Object} mappingData - Category mapping data
   * @param {number} mappingData.id - External category ID
   * @param {string} mappingData.selectedId - Selected Oobyte category ID
   * @param {string} mappingData.selectedParentId - Selected Oobyte parent category ID
   * @returns {Promise} API response
   */
  async updateSupplierCategoryMapping(mappingData) {
    try {
      this._validateCategoryMappingData(mappingData);
      const response = await Vue.axios.put(
        `${this.URL}api/dropshipping/v1/supplierCategory/update`,
        mappingData
      );
      return response;
    } catch (error) {
      console.error("Error updating supplier category mapping:", error);
      throw new Error(`[SupplierApiService] updateSupplierCategoryMapping: ${error.message}`);
    }
  },

  /**
   *
   * @param {string} categoryId - Category ID (Oobyte)
   * @returns
   */
  async automaticProductsPublishing(categoryId) {
    try {
      const response = await Vue.axios.post(
        `${this.URL}api/dropshipping/v1/import/productsByCategory`,
        {
          categoryId: categoryId
        }
      );
      return response;
    } catch (error) {
      console.error("Error automaticProductsPublishing:", error);
      throw new Error(`[SupplierApiService] automaticProductsPublishing: ${error.message}`);
    }
  },

  // ==================== FILTERS ENDPOINTS ====================

  /**
   * Get product filters (categories, vendors, etc.)
   * GET /v1/filter/products
   * @returns {Promise} API response with available filters
   */
  async getProductFilters() {
    try {
      const response = await Vue.axios.get(`${this.URL}api/dropshipping/v1/filter/products`);
      return response;
    } catch (error) {
      console.error("Error fetching product filters:", error);
      throw new Error(`[SupplierApiService] getProductFilters: ${error.message}`);
    }
  },

  /**
   * Get category filters (categories, vendors, etc.)
   * GET /v1/filter/categories
   * @returns {Promise} API response with available filters (for now only vendors)
   */
  async getCategoryFilters() {
    try {
      const response = await Vue.axios.get(`${this.URL}api/dropshipping/v1/filter/categories`);
      return response;
    } catch (error) {
      console.error("Error fetching category filters:", error);
      throw new Error(`[SupplierApiService] getCategoryFilters: ${error.message}`);
    }
  },

  // ==================== UTILITY METHODS ====================

  /**
   * Build query parameters for product list endpoint
   * @private
   */
  _buildProductQueryParams(params) {
    const queryParams = {};

    // Pagination
    if (params.page) queryParams.page = params.page;
    if (params.maxResults) queryParams.maxResults = Math.min(params.maxResults, 100);

    // Filters
    if (params.categories && params.categories.length > 0) {
      params.categories.forEach(categoryId => {
        if (!queryParams.categories) {
          queryParams.categories = [];
        }
        queryParams.categories.push(categoryId);
      });
    }
    if (params.brand) queryParams.brand = params.brand;
    if (params.source) queryParams.source = params.source;
    if (params.fromCostPrice !== null && params.fromCostPrice !== undefined) {
      queryParams.fromCostPrice = params.fromCostPrice;
    }
    if (params.toCostPrice !== null && params.toCostPrice !== undefined) {
      queryParams.toCostPrice = params.toCostPrice;
    }
    if (params.hasQuantity !== null && params.hasQuantity !== undefined) {
      queryParams.hasQuantity = params.hasQuantity;
    }

    return queryParams;
  },

  /**
   * Build query parameters for category list endpoint
   * @private
   */
  _buildCategoryQueryParams(params) {
    const queryParams = {};

    // Pagination
    if (params.page) queryParams.page = params.page;
    if (params.maxResults) queryParams.maxResults = Math.min(params.maxResults, 100);

    // Filters
    if (params.showOnlyRoot !== null && params.showOnlyRoot !== undefined) {
      queryParams.showOnlyRoot = params.showOnlyRoot;
    }

    if (params.name) queryParams.name = params.name;
    if (params.source) queryParams.source = params.source;

    if (params.showUnmapped !== null && params.showUnmapped !== undefined) {
      queryParams.showUnmapped = params.showUnmapped;
    }
    if (params.showReadyToPublish !== null && params.showReadyToPublish !== undefined) {
      queryParams.showReadyToPublish = params.showReadyToPublish;
    }
    if (
      params.showWithUnpublishedProducts !== null &&
      params.showWithUnpublishedProducts !== undefined
    ) {
      queryParams.showWithUnpublishedProducts = params.showWithUnpublishedProducts;
    }

    return queryParams;
  },

  /**
   * Validate product data before sending to API
   * @private
   */
  _validateProductData(productData) {
    const required = ["id"];
    const missing = required.filter(field => !productData[field]);

    if (missing.length > 0) {
      throw new Error(`Missing required fields: ${missing.join(", ")}`);
    }

    // Validate variations if present
    if (productData.variations && Array.isArray(productData.variations)) {
      productData.variations.forEach((variation, index) => {
        if (!variation.id) {
          throw new Error(`Variation at index ${index} is missing required field: id`);
        }
      });
    }

    return true;
  },

  /**
   * Validate category mapping data before sending to API
   * @private
   */
  _validateCategoryMappingData(mappingData) {
    const required = ["id"];
    const missing = required.filter(field => !mappingData[field]);

    if (missing.length > 0) {
      throw new Error(`Missing required fields: ${missing.join(", ")}`);
    }

    return true;
  }
};

export default SupplierApiService;
